/**
 * The confirmation card shown after a sale, and the printed receipt.
 *
 * One component serves both: the screen version is dark and celebratory, the
 * print stylesheet flips it to black-on-white and hides the buttons. The two
 * can never drift apart because there is only one of them.
 *
 * The SK ID and end date are the two things the member actually needs, so they
 * are the two largest things on the card.
 */
import { Link } from 'react-router-dom';
import type { NewSubscriptionResult } from '../db/operations';
import { classLabel } from '@shared/catalog';
import { formatEgp } from '@shared/money';
import { formatDisplayDate, formatLongDate, todayISO } from '@shared/dates';
import { computeRemaining } from '@shared/pricing';
import { formatPhone } from '@shared/identity';
import { PAYMENT_METHOD_LABELS } from '@shared/types';

export default function Receipt({
  result,
  onNew,
}: {
  result: NewSubscriptionResult;
  onNew: () => void;
}) {
  const { member, subscription, payment, createdMember } = result;
  const paid = payment?.amount_piastres ?? 0;
  const remaining = computeRemaining(subscription.total_due_piastres, paid);
  // The schedule price for the whole term, before any discount.
  const subtotal = subscription.price_piastres;

  return (
    <div className="mx-auto max-w-2xl px-5 py-8">
      <div className="no-print mb-6 text-center">
        <div className="mb-2 text-5xl">✓</div>
        <h1 className="text-3xl text-brand">
          {createdMember ? 'Member added' : 'Subscription added'}
        </h1>
      </div>

      <div className="receipt card p-8">
        {/* Print-only letterhead */}
        <div className="print-only mb-6 border-b-2 border-black pb-4 text-center">
          <img src="/logo.png" alt="" className="mx-auto mb-2 h-16 w-auto" />
          <div className="text-xl font-bold">SK MARTIAL ARTS ACADEMY</div>
          <div className="text-sm">Subscription receipt</div>
        </div>

        {/* The two headline facts */}
        <div className="mb-6 grid gap-4 sm:grid-cols-2">
          <div className="rounded-btn border border-edge p-4 text-center">
            <div className="label mb-1">Member ID</div>
            <div className="tabular text-4xl font-bold text-brand">{member.sk_id}</div>
          </div>
          <div className="rounded-btn border border-edge p-4 text-center">
            <div className="label mb-1">Valid until</div>
            <div className="text-2xl font-bold leading-tight">
              {formatDisplayDate(subscription.end_date)}
            </div>
          </div>
        </div>

        <dl className="mb-6 space-y-2 text-sm">
          <Line label="Name">
            <span dir="auto">{member.full_name}</span>
          </Line>
          {member.phone && <Line label="Phone">{formatPhone(member.phone)}</Line>}
          <Line label="Class">
            {classLabel(subscription.class_key)}
            {subscription.private ? ' · Private' : ''}
          </Line>
          <Line label="Rate">
            {subscription.club_member ? 'Club house member' : 'Non-member'}
          </Line>
          <Line label="Term">
            {subscription.months} {subscription.months === 1 ? 'month' : 'months'} ·{' '}
            {formatDisplayDate(subscription.start_date)} →{' '}
            {formatDisplayDate(subscription.end_date)}
          </Line>
        </dl>

        <dl className="space-y-2 border-t border-edge pt-4 text-sm">
          <Line
            label={`${subscription.months} month${subscription.months === 1 ? '' : 's'}`}
          >
            {formatEgp(subtotal)}
          </Line>
          {subscription.discount_piastres > 0 && (
            <Line label="Discount">−{formatEgp(subscription.discount_piastres)}</Line>
          )}
          <div className="flex justify-between border-t border-edge pt-2 text-lg font-bold">
            <dt>Total</dt>
            <dd className="tabular">{formatEgp(subscription.total_due_piastres)}</dd>
          </div>
          <Line label={`Paid${payment ? ` (${PAYMENT_METHOD_LABELS[payment.method]})` : ''}`}>
            {formatEgp(paid)}
          </Line>
        </dl>

        {remaining > 0 ? (
          <div className="mt-4 rounded-btn border border-status-due/50 bg-status-dueBg p-4 text-center">
            <div className="label mb-1">Balance due</div>
            <div className="tabular text-2xl font-bold text-status-due">
              {formatEgp(remaining)}
            </div>
          </div>
        ) : (
          <div className="mt-4 rounded-btn border border-status-active/50 bg-status-activeBg p-3 text-center font-bold text-status-active">
            Paid in full
          </div>
        )}

        <div className="print-only mt-6 border-t border-black pt-4 text-center text-xs">
          Issued {formatLongDate(todayISO())}
          {payment?.received_by ? ` · Received by ${payment.received_by}` : ''}
        </div>
      </div>

      <div className="no-print mt-6 flex flex-wrap gap-3">
        <button onClick={onNew} className="btn flex-1">
          Add another
        </button>
        <button onClick={() => window.print()} className="btn-ghost flex-1">
          Print
        </button>
        <Link to={`/members/${member.id}`} className="btn-ghost flex-1">
          Open profile
        </Link>
      </div>
    </div>
  );
}

function Line({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-white/50">{label}</dt>
      <dd className="tabular text-right font-semibold">{children}</dd>
    </div>
  );
}
